import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getClockingId, getJobId, handleThunkError } from '../common/util';
import { IRepairOrderSyncStatus, IRepairOrder } from '../models/dms/repairOrder.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { RootState } from '.';
import { setIsLoadingCountState } from './appSlice';
import { getRepairOrdersAll } from '../api/dms/repairOrder.api';
import { logConsole, logTransaction } from '../common/log';
import { ADHOC_JOB_CODE, IAdhocJob } from '../models/dms/adhocJob.model';
import { IRepairOrderJob } from '../models/dms/repairOrderJob.model';
import { LogType } from '../models/log.model';

interface IRepairOrderSyncState {
  repairOrderSync?: IRepairOrderSyncStatus;
  repairOrderSyncLoading: boolean;
}

const initialState: IRepairOrderSyncState = {
  repairOrderSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: [] as IRepairOrder[],
  },
  repairOrderSyncLoading: false,
};

/// THUNKS

export const setRepairOrderSyncStoreAsync = createAsyncThunk<{}, IRepairOrderSyncStatus | undefined>('repairOrderSync/setRepairOrderSyncStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting repair orders from sync to storage`, '[repairOrderSync] setRepairOrderSyncStoreAsync');
  try {
    dispatch(setRepairOrderSyncState(val));
    await updateConfigObject<IRepairOrderSyncStatus>(ConfigName.RepairOrderSync, val || ({} as IRepairOrderSyncStatus), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setRepairOrderIsProcessingStoreAsync = createAsyncThunk<{}, boolean>('repairOrderSync/setRepairOrderProcessingAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[repairOrderSync] setRepairOrderProcessingAsync');
  try {
    dispatch(setRepairOrderIsProcessingState(val));
    await updateConfigObject<IRepairOrderSyncStatus>(
      ConfigName.RepairOrderSync,
      {
        ...(getState() as RootState).repairOrderSync.repairOrderSync,
        isProcessing: val,
      } as IRepairOrderSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setRepairOrderHasSyncStoreAsync = createAsyncThunk<{}, boolean>('repairOrderSync/setRepairOrderHasSyncStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[repairOrderSync] setRepairOrderHasSyncStoreAsync');
  try {
    dispatch(setRepairOrderHasSyncState(val));
    await updateConfigObject<IRepairOrderSyncStatus>(
      ConfigName.RepairOrderSync,
      {
        ...(getState() as RootState).repairOrderSync.repairOrderSync,
        hasSync: val,
        isProcessing: false,
      } as IRepairOrderSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const addCustomRepairOrderToRepairOrderSyncStoreAsync = createAsyncThunk<{}, IRepairOrder>('repairOrderSync/addNewRepairOrderToRepairOrderSyncStoreAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Adding custom repair order to repair order sync storage`, '[repairOrderSync] addNewRepairOrderToRepairOrderSyncStoreAsync');
  try {
    let repairOrderSyncState = { ...(getState() as RootState).repairOrderSync.repairOrderSync } as IRepairOrderSyncStatus;
    let repairOrderSyncData = [...repairOrderSyncState.data];
    // Remove from list if already exists
    const customIndex = repairOrderSyncData.findIndex((ro) => ro.repairOrderNumber === payload.repairOrderNumber);
    if (customIndex >= 0) {
      repairOrderSyncData.splice(customIndex, 1);
    }
    repairOrderSyncState = {
      ...repairOrderSyncState,
      timestamp: new Date().getTime(),
      hasSync: true,
      isProcessing: false,
      data: [
        {
          ...payload,
          fromDetail: true,
        },
        ...repairOrderSyncData,
      ],
    };

    dispatch(setRepairOrderSyncState(repairOrderSyncState));
    await updateConfigObject<IRepairOrderSyncStatus>(ConfigName.RepairOrderSync, repairOrderSyncState, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const addAdhocRepairOrderToRepairOrderSyncStoreAsync = createAsyncThunk<{}, IAdhocJob>('repairOrderSync/addAdhocRepairOrderToRepairOrderSyncStoreAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Adding adhoc repair order to repair order sync storage - customerName: ${payload.customerName}, repairOrderNumber: ${payload.repairOrderNumber}`, '[repairOrderSync] addAdhocRepairOrderToRepairOrderSyncStoreAsync');
  try {
    const state = getState() as RootState;
    const { labourClocking } = state.technicianClocking;
    let repairOrderSyncState = { ...state.repairOrderSync.repairOrderSync } as IRepairOrderSyncStatus;
    let repairOrderSyncData = [...repairOrderSyncState.data];

    // Find any adhoc ROs stored in cache that are not clocked on and remove them being sure to keep any adhoc ROs that are still being worked on
    var getAdhocIndex = () =>
      repairOrderSyncData.findIndex((ro) => {
        return (
          ro.isAdhoc &&
          ro.jobs.findIndex((job) => {
            return getJobId(job) === getClockingId(labourClocking);
          }) < 0
        );
      });
    var adhocIndex = getAdhocIndex();
    while (adhocIndex >= 0) {
      repairOrderSyncData.splice(adhocIndex, 1);
      adhocIndex = getAdhocIndex();
    }

    // If the adhoc RO number matches an existing adhoc RO number in cache, add the jobs to this RO rather than creating a new RO
    let matchingExistingRoCache: IRepairOrder | undefined = undefined;
    const matchingExistingRoCacheIndex = repairOrderSyncData.findIndex((ro) => ro.isAdhoc && ro.repairOrderNumber === payload.repairOrderNumber);
    if (matchingExistingRoCacheIndex >= 0) {
      logTransaction(LogType.Info, `The new adhoc repair order number matches an existing repair order in cache. Adding the job information to this existing repair order`);
      matchingExistingRoCache = { ...repairOrderSyncData[matchingExistingRoCacheIndex] };

      // Update with latest data
      matchingExistingRoCache = {
        ...matchingExistingRoCache,
        registrationNumber: payload.registrationNumber,
        vehicle: {
          description: payload.vehicleDetails,
          vin: payload.vin,
        },
        driver: {
          name: payload.customerName,
        },
      } as IRepairOrder;

      // If the job exists, ignore, else add the new job to the existing RO
      const jobId = getJobId({ jobType: payload.customerName, jobCode: ADHOC_JOB_CODE, repairOrderNumber: payload.repairOrderNumber } as IRepairOrderJob);
      const matchingAdhocJob = matchingExistingRoCache.jobs.find((job) => getJobId(job) === jobId);

      if (!matchingAdhocJob) {
        matchingExistingRoCache.jobs = [
          ...matchingExistingRoCache.jobs,
          {
            jobType: payload.customerName,
            jobCode: ADHOC_JOB_CODE,
            repairOrderNumber: payload.repairOrderNumber,
            isAdhoc: true,
          } as IRepairOrderJob,
        ];
      } else {
        logTransaction(LogType.Info, `The new adhoc repair order job '${jobId}' matches an existing job in cache. Not adding this job to this existing repair order`);
      }
    }

    // Use existing or create new RO to add to sync state
    matchingExistingRoCache =
      matchingExistingRoCache ||
      ({
        repairOrderNumber: payload.repairOrderNumber,
        registrationNumber: payload.registrationNumber,
        vehicle: {
          description: payload.vehicleDetails,
          vin: payload.vin,
        },
        driver: {
          name: payload.customerName,
        },
        jobs: [
          {
            jobType: payload.customerName,
            jobCode: ADHOC_JOB_CODE,
            repairOrderNumber: payload.repairOrderNumber,
            isAdhoc: true,
          } as IRepairOrderJob,
        ],
        isAdhoc: true,
      } as IRepairOrder);

    // Update sync state
    repairOrderSyncState = {
      ...repairOrderSyncState,
      timestamp: new Date().getTime(),
      hasSync: true,
      isProcessing: false,
      data: [matchingExistingRoCache, ...repairOrderSyncData],
    };

    dispatch(setRepairOrderSyncState(repairOrderSyncState));
    await updateConfigObject<IRepairOrderSyncStatus>(ConfigName.RepairOrderSync, repairOrderSyncState, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const cleanRepairOrderSyncStoreAsync = createAsyncThunk<{}, number>('repairOrderSync/cleanRepairOrderSyncStoreAsync', async (repairOrderNumberToKeep, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Cleaning non-detail repair orders from sync, keeping repair order '${repairOrderNumberToKeep}'`, '[repairOrderSync] cleanRepairOrderSyncStoreAsync');
  try {
    let repairOrderSyncState = { ...(getState() as RootState).repairOrderSync.repairOrderSync } as IRepairOrderSyncStatus;
    let repairOrderSyncData = [...repairOrderSyncState.data];
    repairOrderSyncData = repairOrderSyncData.filter((ro) => ro.repairOrderNumber === repairOrderNumberToKeep || (ro.repairOrderNumber !== repairOrderNumberToKeep && !ro.fromDetail));
    repairOrderSyncState = {
      ...repairOrderSyncState,
      data: [...repairOrderSyncData],
    };

    dispatch(setRepairOrderSyncState(repairOrderSyncState));
    await updateConfigObject<IRepairOrderSyncStatus>(ConfigName.RepairOrderSync, repairOrderSyncState, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchRepairOrdersStoreAsync = createAsyncThunk<{}, void>('repairOrderSync/fetchRepairOrdersStoreAsync', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching most recent repair orders from database`, '[repairOrderSync] fetchRepairOrdersStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setRepairOrderSyncLoadingState(true));

    let repairOrderSyncState = { ...(getState() as RootState).repairOrderSync.repairOrderSync } as IRepairOrderSyncStatus;
    let lastUpdatedTs = 0;
    let databaseOffsetMinutes = 0;

    lastUpdatedTs = (getState() as RootState).repairOrderSync.repairOrderSync?.timestamp || 0;
    databaseOffsetMinutes = (getState() as RootState).systemConfigurationsSync.systemConfigurationsSync?.data.databaseTimeOffset || 0;
    let days = 60;
    logTransaction(LogType.Info, `Fetching repair orders for - lastUpdatedTs: ${lastUpdatedTs}, databaseOffsetMinutes: ${databaseOffsetMinutes}, days: ${days}`);
    if (!(getState() as RootState).settings.enableOfflineMode) {
      days = 7;
      logTransaction(LogType.Info, `Offline mode is disabled, setting days to '${days}'`);
    }

    try {
      const responseData = await getRepairOrdersAll(lastUpdatedTs, databaseOffsetMinutes, days);

      if (!responseData.deletedRos || !responseData.invalidRos || !responseData.validRos) {
        // No data, do not process
        return;
      }

      const existingRos = [...((getState() as RootState).repairOrderSync.repairOrderSync?.data || [])];
      //const roSearch = responseData.roSearch;
      const validRoNumbers = responseData.validRos?.items.map((ro) => ro.repairOrderNumber) || [];
      let invalidRoNumbers = responseData.invalidRos?.items.map((ro) => ro.repairOrderNumber) || [];
      const deletedRoNumbers = responseData.deletedRos || [];

      // ROs that are no longer relevant (invalid) must be removed. ROs that already exist must be updated.
      // Do this by deleting the invalid and updated ROs and the re-add the updated ones back in
      // Delete the matching ROs
      const existingRosWithUpdatedRemoved = existingRos.filter((ro) => {
        // Keep any ROs that are
        return (
          (!validRoNumbers.includes(ro.repairOrderNumber) && // not included in the validRoNumbers
            !invalidRoNumbers.includes(ro.repairOrderNumber) && // not included in the invalidRoNumbers
            !deletedRoNumbers.includes(ro.repairOrderNumber || -1)) || // not included in the deletedRoNumbers
          ro.fromDetail
        ); // flagged as from detail search
      });

      // Add the matching ROs back in
      let existingRosWithUpdatedAdded = [...existingRosWithUpdatedRemoved, ...(responseData.validRos?.items || [])];

      repairOrderSyncState = {
        ...repairOrderSyncState,
        timestamp: new Date().getTime(),
        hasSync: true,
        isProcessing: false,
        data: [...existingRosWithUpdatedAdded],
      };

      dispatch(setRepairOrderSyncState(repairOrderSyncState));
      await updateConfigObject<IRepairOrderSyncStatus>(ConfigName.RepairOrderSync, repairOrderSyncState, false);
      return responseData;
    } catch (ex: any) {
      logTransaction(LogType.Error, `Failed to execute fetchRepairOrdersStoreAsync thunk. ${ex?.message}`);
    } finally {
      dispatch(setIsLoadingCountState(-1));
      dispatch(setRepairOrderSyncLoadingState(false));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setRepairOrderSyncLoadingState(false));
  }
});

export const updateJobsInRepairOrderSyncStoreAsync = createAsyncThunk<{}, { repairOrderNumber: number; jobs: IRepairOrderJob[] }>(
  'repairOrderSync/updateJobsInRepairOrderSyncStoreAsync',
  async ({ repairOrderNumber, jobs }, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Updating the jobs for ro ${repairOrderNumber} in the sync store`, '[repairOrderSync] updateJobsInRepairOrderSyncStoreAsync');
    try {
      let repairOrderSyncState = { ...(getState() as RootState).repairOrderSync.repairOrderSync } as IRepairOrderSyncStatus;
      let repairOrderSyncData = [...repairOrderSyncState.data];

      const customIndex = repairOrderSyncData.findIndex((ro) => ro.repairOrderNumber === repairOrderNumber && !ro.isAdhoc);
      const syncItem = repairOrderSyncData[customIndex];
      if (syncItem) {
        repairOrderSyncData.splice(customIndex, 1);

        repairOrderSyncState = {
          ...repairOrderSyncState,
          data: [
            {
              ...syncItem,
              jobs: [...jobs],
            },
            ...repairOrderSyncData,
          ],
        };

        dispatch(setRepairOrderSyncState(repairOrderSyncState));
        await updateConfigObject<IRepairOrderSyncStatus>(ConfigName.RepairOrderSync, repairOrderSyncState, false);
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

/// SLICE

export const repairOrderSyncSlice = createSlice({
  name: 'repairOrderSync',
  initialState,
  reducers: {
    setRepairOrderSyncState: (state, action: PayloadAction<IRepairOrderSyncStatus | undefined>) => {
      logConsole(LogType.Info, '[repairOrderSync] setRepairOrderSyncState');
      state.repairOrderSync = action.payload;
    },
    setRepairOrderIsProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[repairOrderSync] setRepairOrderIsProcessingState: ${action.payload}`);
      if (state.repairOrderSync) {
        state.repairOrderSync.isProcessing = action.payload;
      }
    },
    setRepairOrderHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[repairOrderSync] setRepairOrderHasSyncState: ${action.payload}`);
      if (state.repairOrderSync) {
        state.repairOrderSync.hasSync = action.payload;
        state.repairOrderSync.isProcessing = false;
      }
    },
    setRepairOrderSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[repairOrderSync] setRepairOrderSyncLoadingState: ${action.payload}`);
      state.repairOrderSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setRepairOrderSyncState, setRepairOrderIsProcessingState, setRepairOrderHasSyncState, setRepairOrderSyncLoadingState } = repairOrderSyncSlice.actions;

export default repairOrderSyncSlice.reducer;
